import { useState, useEffect } from "react";
import styles from "./Orders.module.scss";
import Loading from "../../components/common/loading/Loading";
import TitleUpdater from "../../components/common/title/TitleUpdater";
import AccountSidebar from "../../components/common/account-sidebar/AccountSidebar";
import { useTranslation } from "react-i18next";
import OrderService from "../../api/services/order.service";
import { toast } from "sonner";

interface User {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
}

interface Country {
  id: number;
  name: string;
  created: string;
}

interface City {
  id: number;
  name: string;
  country_id: number;
  country: Country;
  created: string;
}

interface Address {
  id: number;
  user: User;
  city: City;
  zip_code: string;
  address: string;
  phone: string;
}

interface ProductVariant {
  id: number;
  product_id: number;
  name_en: string;
  sku: string;
  price: number;
}

interface Store {
  id: number;
  name: string;
  email: string;
}

interface Order {
  id: number;
  user_id: number;
  store_id: number;
  product_variant_id: number;
  quantity: number;
  price: number;
  currency_name: string;
  payment_status: string;
  status: string;
  address: Address;
  product_variant: ProductVariant;
  store: Store;
  created: string;
}

interface Payment {
  id: number;
  user_id: number;
  order_id: number;
  amount: number;
  currency_name: string;
  status: string;
  transaction_id: string;
  card_number: string;
  created: string;
}

interface PaymentGroup {
  payment: Payment;
  orders: Order[];
}

const Orders = () => {
  const [loading, setLoading] = useState(false);
  const [paymentGroups, setPaymentGroups] = useState<PaymentGroup[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const { t, i18n } = useTranslation("translation");
  const locale = i18n.resolvedLanguage || i18n.language || undefined;

  useEffect(() => {
    setLoading(true);
    const getData = async () => {
      try {
        const res = await OrderService.getUserOrders();
        setPaymentGroups(res.data || []);
      } catch (err) {
        console.error(err);
        toast.error(t("common_error") || "Xəta baş verdi");
      } finally {
        setLoading(false);
      }
    };
    getData();
  }, [t]);

  const getSafeTranslation = (
    key: string,
    fallbackKey: string,
    fallbackText: string,
  ) => {
    const translated = t(key);
    if (translated && translated !== key) {
      return translated;
    }

    const fallbackTranslated = t(fallbackKey);
    if (fallbackTranslated && fallbackTranslated !== fallbackKey) {
      return fallbackTranslated;
    }

    return fallbackText;
  };

  const getStatusText = (status: string) => {
    return getSafeTranslation(
      `order_status.${status}`,
      "order_status.7",
      "Pending",
    );
  };

  const getPaymentStatusText = (status: string) => {
    return getSafeTranslation(
      `payment_status.${status}`,
      "payment_status.3",
      "Pending",
    );
  };

  const getPaymentStatusClass = (status: string) => {
    switch (status) {
      case "1":
        return styles.statusSuccess;
      case "2":
        return styles.statusFailed;
      case "3":
        return styles.statusPending;
      default:
        return styles.statusPending;
    }
  };

  const getOrderStatusClass = (status: string) => {
    switch (status) {
      case "6": // Tamamlandı
        return styles.statusSuccess;
      case "5": // Çatdırıldı
        return styles.statusDelivered;
      case "4": // Çatdırılır
        return styles.statusDelivering;
      case "1": // Qəbul edildi
      case "2": // Paketlənir
      case "3": // Admin üçün hazır
        return styles.statusProcessing;
      case "7": // Gözləyir
        return styles.statusPending;
      default:
        return styles.statusPending;
    }
  };

  const handleBackToList = () => {
    setSelectedOrder(null);
  };

  if (loading) {
    return <Loading />;
  }

  // Detail view - Sifarişə bax
  if (selectedOrder) {
    const paymentGroup = paymentGroups.find((pg) =>
      pg.orders.some((o) => o.id === selectedOrder.id),
    );

    return (
      <div className={styles.orders}>
        <TitleUpdater title={t("orders") || "Sifarişlərim"} />
        <div className="container">
          <div className={styles.wrapper}>
            <AccountSidebar />
            <div className={styles.content}>
              <div className={styles.detailHeader}>
                <button
                  onClick={handleBackToList}
                  className={styles.backButton}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M15 18L9 12L15 6"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  {t("order_detail.back_to_orders")}
                </button>
              </div>

              <div className={styles.orderIdRow}>
                <span className={styles.orderIdLabel}>
                  {t("order_detail.order_id")}:{" "}
                  <strong>#{selectedOrder.id}</strong>
                </span>
                <span className={styles.orderDate}>
                  {t("my_orders.date")}:{" "}
                  {new Date(selectedOrder.created).toLocaleDateString(locale, {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                  })}{" "}
                  {new Date(selectedOrder.created).toLocaleTimeString(locale, {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
              </div>

              <div className={styles.detailGrid}>
                <div className={styles.detailSection}>
                  <h3>{t("order_detail.title")}</h3>
                  <div className={styles.detailRow}>
                    <span className={styles.label}>
                      {t("order_detail.order_date")}
                    </span>
                    <span className={styles.value}>
                      {new Date(selectedOrder.created).toLocaleDateString(
                        locale,
                        {
                          year: "numeric",
                          month: "2-digit",
                          day: "2-digit",
                        },
                      )}{" "}
                      {new Date(selectedOrder.created).toLocaleTimeString(
                        locale,
                        {
                          hour: "2-digit",
                          minute: "2-digit",
                        },
                      )}
                    </span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.label}>
                      {t("order_detail.product")}
                    </span>
                    <span className={styles.value}>
                      {selectedOrder.product_variant.name_en}
                    </span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.label}>
                      {t("order_detail.quantity")}
                    </span>
                    <span className={styles.value}>
                      x{selectedOrder.quantity}
                    </span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.label}>
                      {t("order_detail.total_amount")}
                    </span>
                    <span className={styles.value}>
                      {selectedOrder.price} {selectedOrder.currency_name}
                    </span>
                  </div>
                </div>

                <div className={styles.detailSection}>
                  <h3>{t("order_detail.customer_info")}</h3>
                  <div className={styles.detailRow}>
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M10 10C12.7614 10 15 7.76142 15 5C15 2.23858 12.7614 0 10 0C7.23858 0 5 2.23858 5 5C5 7.76142 7.23858 10 10 10Z"
                        fill="#64748B"
                      />
                      <path
                        d="M10 12C4.477 12 0 14.686 0 18V20H20V18C20 14.686 15.523 12 10 12Z"
                        fill="#64748B"
                      />
                    </svg>
                    <span className={styles.value}>
                      {selectedOrder.address.user.first_name}{" "}
                      {selectedOrder.address.user.last_name}
                    </span>
                  </div>
                  <div className={styles.detailRow}>
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M18 4H2C0.9 4 0 4.9 0 6V14C0 15.1 0.9 16 2 16H18C19.1 16 20 15.1 20 14V6C20 4.9 19.1 4 18 4ZM18 8L10 12L2 8V6L10 10L18 6V8Z"
                        fill="#64748B"
                      />
                    </svg>
                    <span className={styles.value}>
                      {selectedOrder.address.user.email}
                    </span>
                  </div>
                  <div className={styles.detailRow}>
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M18 12.5C16.5 12.5 15.1 12.3 13.8 11.9C13.4 11.8 13 11.9 12.7 12.2L10.2 14.7C7.4 13.3 6.7 12.6 5.3 9.8L7.8 7.3C8.1 7 8.2 6.6 8.1 6.2C7.7 4.9 7.5 3.5 7.5 2C7.5 1.2 6.8 0.5 6 0.5H2C1.2 0.5 0.5 1.2 0.5 2C0.5 11.9 8.1 19.5 18 19.5C18.8 19.5 19.5 18.8 19.5 18V14C19.5 13.2 18.8 12.5 18 12.5Z"
                        fill="#64748B"
                      />
                    </svg>
                    <span className={styles.value}>
                      {selectedOrder.address.phone}
                    </span>
                  </div>
                </div>

                <div className={styles.detailSection}>
                  <h3>{t("order_detail.delivery_address")}</h3>
                  <div className={styles.detailRow}>
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M10 10C12.7614 10 15 7.76142 15 5C15 2.23858 12.7614 0 10 0C7.23858 0 5 2.23858 5 5C5 7.76142 7.23858 10 10 10Z"
                        fill="#64748B"
                      />
                      <path
                        d="M10 12C4.477 12 0 14.686 0 18V20H20V18C20 14.686 15.523 12 10 12Z"
                        fill="#64748B"
                      />
                    </svg>
                    <span className={styles.value}>
                      {selectedOrder.address.user.first_name}{" "}
                      {selectedOrder.address.user.last_name}
                    </span>
                  </div>
                  <div className={styles.addressText}>
                    {selectedOrder.address.city.name}{" "}
                    {selectedOrder.address.city.country.name}
                  </div>
                  <div className={styles.addressText}>
                    {selectedOrder.address.address}
                  </div>
                </div>
              </div>

              <div className={styles.paymentSection}>
                <h3>{t("order_detail.payment_method")}</h3>
                {paymentGroup?.payment && (
                  <div className={styles.paymentCard}>
                    <svg
                      width="40"
                      height="24"
                      viewBox="0 0 40 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect width="40" height="24" rx="4" fill="#1434CB" />
                      <path d="M14.4 8.4H25.6V15.6H14.4V8.4Z" fill="#F79F1A" />
                      <circle cx="14.4" cy="12" r="5.4" fill="#EB001B" />
                      <circle cx="25.6" cy="12" r="5.4" fill="#F79F1A" />
                    </svg>
                    <span className={styles.cardNumber}>
                      Visa •••• {paymentGroup.payment.card_number.slice(-4)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.orders}>
      <TitleUpdater title={t("orders") || "Sifarişlərim"} />
      <div className="container">
        <div className={styles.wrapper}>
          <AccountSidebar />
          <div className={styles.content}>
            <h1 className={styles.pageTitle}>
              {t("orders") || "Sifarişlərim"}
            </h1>

            {/* Empty state - heç sifariş yoxdur */}
            {paymentGroups.length === 0 ? (
              <div className={styles.emptyState}>
                <div className={styles.emptyIcon}>
                  <svg
                    width="120"
                    height="120"
                    viewBox="0 0 120 120"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle cx="60" cy="60" r="60" fill="#E0F2FE" />
                    <path
                      d="M45 35H75C77.7614 35 80 37.2386 80 40V80C80 82.7614 77.7614 85 75 85H45C42.2386 85 40 82.7614 40 80V40C40 37.2386 42.2386 35 45 35Z"
                      fill="#0EA5E9"
                    />
                    <path
                      d="M50 50H70M50 60H70M50 70H65"
                      stroke="white"
                      strokeWidth="3"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
                <h2>{t("no_orders") || "Hələ sifarişiniz yoxdur"}</h2>
                <p>
                  {t("no_orders_desc") || "Sifariş etdikdə burada görünəcək"}
                </p>
              </div>
            ) : (
              /* List view - sifarişlər var */
              <div className={styles.ordersList}>
                {paymentGroups.map((group) => (
                  <div key={group.payment.id} className={styles.orderGroup}>
                    <div className={styles.paymentInfo}>
                      <div className={styles.paymentHeader}>
                        <div className={styles.paymentId}>
                          <span className={styles.label}>
                            {t("order_detail.payment_id")}:
                          </span>
                          <span className={styles.value}>
                            #{group.payment.id}
                          </span>
                        </div>
                        <div className={styles.paymentAmount}>
                          <span className={styles.amount}>
                            {group.payment.amount} {group.payment.currency_name}
                          </span>
                        </div>
                      </div>
                      <div className={styles.paymentDetails}>
                        <div className={styles.paymentDetail}>
                          <span className={styles.label}>
                            {t("my_orders.date")}:
                          </span>
                          <span className={styles.value}>
                            {new Date(group.payment.created).toLocaleDateString(
                              locale,
                              {
                                year: "numeric",
                                month: "2-digit",
                                day: "2-digit",
                              },
                            )}{" "}
                            {new Date(group.payment.created).toLocaleTimeString(
                              locale,
                              {
                                hour: "2-digit",
                                minute: "2-digit",
                              },
                            )}
                          </span>
                        </div>
                        <div className={styles.paymentDetail}>
                          <span className={styles.label}>
                            {t("my_orders.status")}:
                          </span>
                          <span
                            className={`${styles.value} ${styles.statusBadge} ${getPaymentStatusClass(group.payment.status)}`}
                          >
                            {getPaymentStatusText(group.payment.status)}
                          </span>
                        </div>
                        {group.payment.card_number && (
                          <div className={styles.paymentDetail}>
                            <span className={styles.label}>
                              {t("order_detail.card_number")}:
                            </span>
                            <span className={styles.value}>
                              {group.payment.card_number}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className={styles.ordersInGroup}>
                      {group.orders.map((order) => (
                        <div key={order.id} className={styles.orderCard}>
                          <div className={styles.orderHeader}>
                            <div className={styles.orderId}>
                              {t("my_orders.order")} #{order.id}
                            </div>
                            <div
                              className={`${styles.orderStatus} ${getOrderStatusClass(order.status)}`}
                            >
                              {getStatusText(order.status)}
                            </div>
                          </div>
                          <div className={styles.orderBody}>
                            <div className={styles.productInfo}>
                              <div className={styles.productName}>
                                {order.product_variant.name_en}
                              </div>
                              <div className={styles.productDetails}>
                                <span>
                                  {t("order_detail.sku")}:{" "}
                                  {order.product_variant.sku}
                                </span>
                                <span>
                                  {t("my_orders.quantity")}: {order.quantity}
                                </span>
                              </div>
                            </div>
                            <div className={styles.storeInfo}>
                              <span className={styles.storeLabel}>
                                {t("order_detail.store")}:
                              </span>
                              <span className={styles.storeName}>
                                {order.store.name}
                              </span>
                            </div>
                            <div className={styles.orderPrice}>
                              {order.price} {order.currency_name}
                            </div>
                          </div>
                          <div className={styles.orderActions}>
                            <button
                              onClick={() => setSelectedOrder(order)}
                              className={styles.viewButton}
                            >
                              {t("my_orders.view_order")}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Orders;
